// What a client shows, worked out from the plays it holds rather than asked of the server. Each board can
// have several plays (every replay is one), so these decide which of them a screen means.

import type { LoadedPlay, PlayCompletion } from '../types';
import type { PlayResult } from '../dayResults';
import type { SyncedPlay } from './syncedPlay';

export type DayPlayStatus = 'complete' | 'incomplete' | 'not-started';

const newestFirst = (a: SyncedPlay, b: SyncedPlay) => b.createdAt - a.createdAt || (a.cid < b.cid ? 1 : -1);

/** A completion the player has reset to unplayed in the archive calendar. */
const isReset = (play: SyncedPlay) => play.resetAt != null;

/**
 * The play a board opens to: the one started most recently, finished or not. A reset completion is passed
 * over, so a board whose every play was reset opens fresh.
 */
export function activePlay(plays: SyncedPlay[]): SyncedPlay | null {
  const live = plays.filter((play) => !isReset(play));
  return live.length ? live.sort(newestFirst)[0] : null;
}

/** The completions before the active play, newest first, as the play store keeps them. */
export function winsHistory(plays: SyncedPlay[], active: SyncedPlay | null): PlayCompletion[] {
  return [...plays]
    .sort(newestFirst)
    .filter((play) => play.complete && play !== active)
    .map((play) => ({
      completionTime: play.duration,
      completionHintsUsed: play.playData.playStats?.hintsUsed ?? 0,
      completionChecksUsed: play.playData.playStats?.checksUsed ?? 0,
      completedAt: new Date(play.completedAt ?? play.createdAt),
    }));
}

/** A board's plays as the play store loads them: the active play, with the wins before it. */
export function toLoadedPlay(plays: SyncedPlay[]): LoadedPlay | null {
  const active = activePlay(plays);
  if (!active) {
    // Every play was reset: a fresh board, still carrying the wins before it.
    const wins = winsHistory(plays, null);
    if (!wins.length) return null;
    return {
      id: null,
      board_id: plays[0].boardId,
      complete: false,
      play_duration: 0,
      play_data: { cells: null as unknown as unknown[][], playStats: { hintsUsed: 0, checksUsed: 0 } },
      winsHistory: wins,
      moveHistory: [],
      created_at: null,
    };
  }
  return {
    id: null,
    board_id: active.boardId,
    complete: active.complete,
    play_duration: active.duration,
    play_data: active.playData,
    winsHistory: winsHistory(plays, active),
    moveHistory: active.moves ?? [],
    created_at: new Date(active.createdAt).toISOString(),
  };
}

/** Finished once any play of it is; started once any play exists. A replay never un-finishes a day. */
export function dayStatus(plays: SyncedPlay[]): DayPlayStatus {
  if (plays.some((play) => play.complete)) return 'complete';
  return plays.length ? 'incomplete' : 'not-started';
}

/** As dayStatus, but for the archive calendar, where a reset completion counts as never played. */
export function calendarStatus(plays: SyncedPlay[]): DayPlayStatus {
  return dayStatus(plays.filter((play) => !isReset(play)));
}

function toResult(play: SyncedPlay): PlayResult {
  return {
    durationSec: Math.max(0, play.duration),
    hintsUsed: play.playData.playStats?.hintsUsed ?? 0,
    checksUsed: play.playData.playStats?.checksUsed ?? 0,
  };
}

const completionTime = (play: SyncedPlay) => play.completedAt ?? play.createdAt;

/** The most recent completion's numbers, as the week and archive show them. */
export function latestResult(plays: SyncedPlay[]): PlayResult | undefined {
  const done = plays.filter((play) => play.complete).sort((a, b) => completionTime(b) - completionTime(a));
  return done.length ? toResult(done[0]) : undefined;
}

/** The first completion's numbers, as day results and stats count them. */
export function firstResult(plays: SyncedPlay[]): PlayResult | undefined {
  const done = plays.filter((play) => play.complete).sort((a, b) => completionTime(a) - completionTime(b));
  return done.length ? toResult(done[0]) : undefined;
}

/**
 * How a day-results row shows a board. Solved: its first solve's numbers. Reset: every solve was reset in
 * the archive, and the row shows the latest of them while offering a replay. Unsolved: never finished.
 */
export type DayResultState = 'solved' | 'reset' | 'unsolved';

export interface DayResult {
  state: DayResultState;
  /** Present unless unsolved. */
  result?: PlayResult;
  /** The board opens to a play still in progress. */
  inProgress: boolean;
}

export function dayResult(plays: SyncedPlay[]): DayResult {
  const active = activePlay(plays);
  const inProgress = !!active && !active.complete;
  if (plays.some((play) => play.complete && !isReset(play))) {
    return { state: 'solved', result: firstResult(plays), inProgress };
  }
  const latest = latestResult(plays);
  return latest ? { state: 'reset', result: latest, inProgress } : { state: 'unsolved', inProgress };
}
